/*
  Themerie — sorteerdropdown.
  Wraps the native <select> that Shopify's facets form needs and renders a themed
  listbox on top of it, because the OS-rendered <select> popup cannot be styled and
  looks nothing like the filter pills next to it. The native select stays in the DOM
  and keeps receiving `change`, so FacetFiltersForm keeps working; without JS the
  native control simply remains visible.
*/
class ThemerieSort extends HTMLElement {
  connectedCallback() {
    if (this.dataset.enhanced === 'true') return;
    this.select = this.querySelector('select');
    if (!this.select || !this.select.options.length) return;
    this.dataset.enhanced = 'true';

    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.className = 'themerie-sort__button';
    this.button.setAttribute('aria-haspopup', 'listbox');
    this.button.setAttribute('aria-expanded', 'false');

    const label = this.select.id && document.querySelector(`label[for="${this.select.id}"]`);
    if (label) {
      if (!label.id) label.id = `${this.select.id}-label`;
      this.button.setAttribute('aria-labelledby', `${label.id} ${this.select.id}-value`);
    }

    this.value = document.createElement('span');
    this.value.className = 'themerie-sort__value';
    this.value.id = `${this.select.id}-value`;
    this.button.appendChild(this.value);

    const caret = this.querySelector('.svg-wrapper');
    if (caret) this.button.appendChild(caret);

    this.list = document.createElement('ul');
    this.list.className = 'themerie-sort__list';
    this.list.setAttribute('role', 'listbox');
    this.list.hidden = true;

    Array.from(this.select.options).forEach((option, index) => {
      const item = document.createElement('li');
      item.className = 'themerie-sort__option';
      item.setAttribute('role', 'option');
      item.tabIndex = -1;
      item.dataset.index = index;
      item.textContent = option.textContent.trim();
      item.addEventListener('click', () => this.pick(index));
      this.list.appendChild(item);
    });

    this.select.classList.add('themerie-sort__native');
    this.append(this.button, this.list);
    this.sync();

    // stopImmediatePropagation: Dawn's MenuDrawer.bindEvents() (re-run by facets.js after every filter
    // change) wires a "close" handler onto every button in the mobile filter drawer, this one included,
    // which shut the whole drawer on a tap. Ours is registered first, so it can stop that handler.
    this.button.addEventListener('click', (event) => {
      event.stopImmediatePropagation();
      this.toggle(this.list.hidden);
    });
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.select.addEventListener('change', () => this.sync());
    this.onOutside = (event) => {
      if (!this.contains(event.target)) this.toggle(false);
    };
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onOutside);
  }

  sync() {
    const option = this.select.options[this.select.selectedIndex];
    this.value.textContent = option ? option.textContent.trim() : '';
    Array.from(this.list.children).forEach((item, index) => {
      const selected = index === this.select.selectedIndex;
      item.setAttribute('aria-selected', selected ? 'true' : 'false');
      item.classList.toggle('themerie-sort__option--selected', selected);
    });
  }

  toggle(open) {
    this.list.hidden = !open;
    this.button.setAttribute('aria-expanded', open ? 'true' : 'false');
    this.classList.toggle('themerie-sort--open', open);
    if (open) {
      document.addEventListener('click', this.onOutside);
      const current = this.list.children[this.select.selectedIndex];
      if (current) current.focus();
    } else {
      document.removeEventListener('click', this.onOutside);
    }
  }

  pick(index) {
    if (this.select.selectedIndex !== index) {
      this.select.selectedIndex = index;
      // FacetFiltersForm luistert op 'input' (assets/facets.js), Dawn's andere
      // formulieren op 'change'; allebei sturen dus allebei de events.
      this.select.dispatchEvent(new Event('input', { bubbles: true }));
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this.sync();
    this.toggle(false);
    this.button.focus();
  }

  onKeydown(event) {
    const items = Array.from(this.list.children);
    const active = items.indexOf(document.activeElement);

    switch (event.key) {
      case 'Escape':
        if (!this.list.hidden) {
          event.preventDefault();
          this.toggle(false);
          this.button.focus();
        }
        break;
      case 'ArrowDown':
      case 'ArrowUp': {
        event.preventDefault();
        if (this.list.hidden) {
          this.toggle(true);
          break;
        }
        const step = event.key === 'ArrowDown' ? 1 : -1;
        const next = (active + step + items.length) % items.length;
        items[next].focus();
        break;
      }
      case 'Enter':
      case ' ':
        if (!this.list.hidden && active > -1) {
          event.preventDefault();
          this.pick(active);
        }
        break;
      default:
        break;
    }
  }
}

customElements.define('themerie-sort', ThemerieSort);
